'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

export default function ForgotPasswordForm() {
  const supabase = createClient();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback?next=/reset-password`,
      });
      if (resetError) throw resetError;
      setSent(true);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel — Brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary flex-col items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 bg-white translate-x-20 -translate-y-20" />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full opacity-10 bg-white -translate-x-20 translate-y-20" />
        <div className="relative z-10 text-center max-w-md">
          <div className="flex justify-center mb-6">
            <div className="bg-white/20 rounded-3xl p-4 backdrop-blur-sm">
              <AppLogo size={56} />
            </div>
          </div>
          <h1 className="text-4xl font-extrabold text-white mb-3 leading-tight">City Happenings</h1>
          <p className="text-xl text-white/80 font-medium">Your Local Content Engine.</p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-background">
        <div className="w-full max-w-sm">
          {/* Mobile Logo */}
          <div className="flex flex-col items-center mb-8 lg:hidden">
            <div className="flex items-center gap-3 mb-2">
              <AppLogo size={40} />
              <span className="text-2xl font-bold text-foreground">City Happenings</span>
            </div>
          </div>

          {sent ? (
            /* Success State */
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-5">
                <Icon name="EnvelopeIcon" size={32} className="text-emerald-500" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Check your inbox</h2>
              <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                We sent a password reset link to <span className="font-semibold text-foreground">{email}</span>.
                Check your email and follow the link to reset your password.
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                Didn&apos;t receive it? Check your spam folder or{' '}
                <button
                  onClick={() => setSent(false)}
                  className="text-primary font-semibold hover:underline"
                >
                  try again
                </button>.
              </p>
              <Link
                href="/login-screen"
                className="w-full btn-primary py-3 text-sm flex items-center justify-center gap-2"
              >
                <Icon name="ArrowLeftIcon" size={16} />
                Back to Sign In
              </Link>
            </div>
          ) : (
            /* Form State */
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-foreground mb-1">Forgot Password?</h2>
                <p className="text-muted-foreground text-sm">
                  Enter your email and we&apos;ll send you a link to reset your password.
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 flex items-start gap-2">
                  <Icon name="ExclamationCircleIcon" size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-600 font-medium">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="label-text">Email Address</label>
                  <input
                    type="email"
                    placeholder="you@cityhappenings.io"
                    className="input-field"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary py-3.5 text-base flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
                      Sending…
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  href="/login-screen"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1.5"
                >
                  <Icon name="ArrowLeftIcon" size={14} />
                  Back to Sign In
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
